"use client";

import dynamic from "next/dynamic";
import { useAtomValue, useAtom, useSetAtom } from "jotai";
import { useLocale, useTranslations } from "next-intl";
import React, { type ReactElement, useMemo } from "react";
import type { MultiValue, SingleValue } from "react-select";
import { OptionType } from "@oreme/ui";
import {
  selectedObservatoryAtom,
  selectedTagIdsAtom,
  tagsAtom,
} from "@/store/devices";

const MultiSelect = dynamic(
  () => import("@oreme/ui").then((mod) => mod.MultiSelect),
  { ssr: false },
);

function DevicesTagSelect(): ReactElement {
  const locale = useLocale();
  const t = useTranslations("devices.TagSelect");

  const tags = useAtomValue(tagsAtom);
  const [selectedTagIds, setSelectedTagIds] = useAtom(selectedTagIdsAtom);
  const setSelectedObservatory = useSetAtom(selectedObservatoryAtom);

  const options = useMemo(() => {
    return tags
      .map((tag) => ({
        label: tag.name[locale],
        value: tag.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tags, locale]);

  const value = useMemo(() => {
    return options.filter(({ value }) => selectedTagIds.includes(value));
  }, [options, selectedTagIds]);

  const handleChange = (
    newValue: SingleValue<OptionType> | MultiValue<OptionType>,
  ) => {
    if (!Array.isArray(newValue)) return;

    if (selectedTagIds.length === 0) {
      setSelectedObservatory(null);
    }
    const newSelectedTagIds = newValue.map(({ value }) => Number(value));
    setSelectedTagIds(newSelectedTagIds);
  };

  return (
    <div className="flex flex-col gap-1">
      <header className="flex flex-row justify-between items-center min-h-6">
        <h3 className="text-xs">{t("title")}</h3>
      </header>
      <MultiSelect
        unstyled={true}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        className="relative z-50 w-full"
        value={value}
        onChange={handleChange}
        options={options}
        placeholder={t("searchPlaceholder")}
        noOptionsMessage={() => t("noOptionsMessage")}
        menuPlacement="auto"
        menuPortalTarget={
          globalThis.window === undefined ? null : document.body
        }
        isMulti
        isSearchable
      />
    </div>
  );
}

export { DevicesTagSelect };
